import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Stack,
  InputAdornment,
  Avatar,
  Collapse,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Restore,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';

// 실제 데이터베이스 스키마에 맞는 인터페이스
interface Product {
  product_id: number;
  product_name: string;
  price: number;
  status: string;
  main_images: string[];
  detail_images: string[]; // detail_images 추가
  store_name: string;
  description: string;
  keywords: string[];
  created_at: string;
  updated_at: string;
  is_d2c?: boolean;
  external_url?: string;
}

interface ProductOption {
  id: number;
  product_id: number;
  color_name: string;
  size: string;
  quantity: number;
  color_hex: string;
  created_at: string;
  updated_at: string;
}

const statusLabel = (status: string, onClick?: () => void) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Active', color: 'success' as any };
      case 'inactive':
        return { label: 'Inactive', color: 'default' as any };
      case 'trashed':
        return { label: 'Trashed', color: 'error' as any };
      default:
        return { label: 'Pending', color: 'warning' as any };
    }
  };
  
  const { label, color } = getStatusConfig(status);
  
  const chipProps = {
    label,
    color,
    size: 'small' as const,
    sx: { 
      fontWeight: 700, 
      borderRadius: 1,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { opacity: 0.8 } : {}
    },
    onClick: onClick
  };
  
  return <Chip {...chipProps} />;
};

// 합산 재고 계산 함수
const calculateTotalStock = (options: ProductOption[]): number => {
  return options.reduce((total, option) => total + option.quantity, 0);
};

// 확장 가능한 행 컴포넌트
function ExpandableRow({ product, options, onEdit, onDelete, onStatusChange, onRestore, isEditing, editingProductId, onEditOption, onDeleteOption, onCopyLink }: {
  product: Product;
  options: ProductOption[];
  onEdit: (product: Product) => void;
  onDelete: (productId: number) => void;
  onStatusChange: (productId: number, newStatus: string) => void;
  onRestore?: (productId: number) => void;
  isEditing: boolean;
  editingProductId: number | null;
  onEditOption: (option: ProductOption) => void;
  onDeleteOption: (optionId: number) => void;
  onCopyLink: (productId: number, productName: string) => void;
}) {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [expanded, setExpanded] = useState(false);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const handleStatusClick = () => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active';
    onStatusChange(product.product_id, newStatus);
  };

  return (
    <>
      <TableRow>
        <TableCell sx={styles.table.cell}>
          <IconButton
            size="small"
            onClick={handleExpand}
            sx={{ color: styles.text.primary }}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={styles.table.cell}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={product.main_images?.[0] || ''}
              alt={product.product_name}
              sx={{ width: 50, height: 50 }}
            />
            <Box>
              <Typography variant="body2" fontWeight={600} color={styles.text.primary}>
                {product.product_name}
              </Typography>
              <Typography variant="caption" color={styles.text.secondary}>
                {product.store_name}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={styles.table.cell}>
          <Typography color={styles.text.primary}>
            {product.price.toLocaleString()}{t('sellerProducts.currencyUnit')}
          </Typography>
        </TableCell>
        <TableCell sx={styles.table.cell}>
          {options.length > 0 ? (
            <Typography variant="body2" fontWeight={600} color={styles.text.primary}>
              {calculateTotalStock(options).toLocaleString()}{t('sellerProducts.itemUnit')}
            </Typography>
          ) : (
            <Typography variant="body2" color={styles.text.secondary}>
              {t('sellerProducts.noOption')}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={styles.table.cell}>{statusLabel(product.status, handleStatusClick)}</TableCell>
        <TableCell sx={styles.table.cell}>
          {product.status === 'trashed' ? (
            // 휴지통에 있는 상품의 경우 복원 버튼만 표시
            <IconButton
              size="small"
              onClick={() => onRestore && onRestore(product.product_id)}
              sx={{ color: '#4caf50' }}
              title={t('sellerProducts.restore')}
            >
              <Restore />
            </IconButton>
          ) : (
            // 일반 상품의 경우 링크, 편집 및 삭제 버튼 표시
            <>
              <IconButton
                size="small"
                onClick={() => onCopyLink(product.product_id, product.product_name)}
                sx={{ color: '#2196f3' }}
                title={t('sellerProducts.copyLink')}
              >
                <LinkIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onEdit(product)}
                disabled={isEditing && editingProductId === product.product_id}
                sx={{ color: styles.text.primary }}
                title="편집"
              >
                {isEditing && editingProductId === product.product_id ? (
                  <CircularProgress size={16} />
                ) : (
                  <Edit />
                )}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onDelete(product.product_id)}
                sx={{ color: styles.text.primary }}
                title={t('common.delete')}
              >
                <Delete />
              </IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, ...styles.table.cell }} colSpan={6}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div" color={styles.text.primary}>
                {t('sellerProducts.productOptions')}
              </Typography>
              <Table size="small">
                  <TableHead>
                    <TableRow sx={styles.table.header}>
                      <TableCell sx={styles.table.cell}>{t('sellerProducts.color')}</TableCell>
                      <TableCell sx={styles.table.cell}>{t('sellerProducts.size')}</TableCell>
                      <TableCell sx={styles.table.cell}>{t('sellerProducts.quantity')}</TableCell>
                      <TableCell sx={styles.table.cell}>{t('sellerProducts.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {options.map((option) => (
                      <TableRow key={option.id}>
                        <TableCell sx={styles.table.cell}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: option.color_hex || '#ccc',
                                border: `1px solid ${isDarkMode ? '#606060' : '#ddd'}`
                              }}
                            />
                            <Typography color={styles.text.primary}>{option.color_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={styles.table.cell}>
                          <Typography color={styles.text.primary}>{option.size}</Typography>
                        </TableCell>
                        <TableCell sx={styles.table.cell}>
                          <Typography color={styles.text.primary}>{option.quantity}</Typography>
                        </TableCell>
                        <TableCell sx={styles.table.cell}>
                          <IconButton
                            size="small"
                            sx={{ color: styles.text.primary }}
                            onClick={() => onEditOption(option)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: styles.text.primary }}
                            onClick={() => onDeleteOption(option.id)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function NewProductDialog({ open, onClose, onSave }: { open: boolean, onClose: () => void, onSave: (data: any) => void }) {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [mainImages, setMainImages] = useState<File[]>([]);
  const [detailImages, setDetailImages] = useState<File[]>([]);
  const [optionInput, setOptionInput] = useState({ color: '', size: '', quantity: '' });
  const [options, setOptions] = useState<{ color: string, size: string, quantity: string }[]>([]);

  // 대표 이미지 업로드
  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setMainImages(prev => [...prev, ...files].slice(0, 5));
  };
  // 상세 이미지 업로드
  const handleDetailImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setDetailImages(prev => [...prev, ...files].slice(0, 10));
  };
  // 옵션 추가
  const handleAddOption = () => {
    if (!optionInput.color && !optionInput.size) return;
    setOptions(list => [...list, optionInput]);
    setOptionInput({ color: '', size: '', quantity: '' });
  };
  // 옵션 삭제
  const handleRemoveOption = (idx: number) => {
    setOptions(list => list.filter((_, i) => i !== idx));
  };
  // 대표 이미지 삭제
  const handleRemoveMainImage = (idx: number) => {
    setMainImages(list => list.filter((_, i) => i !== idx));
  };
  // 상세 이미지 삭제
  const handleRemoveDetailImage = (idx: number) => {
    setDetailImages(list => list.filter((_, i) => i !== idx));
  };
  // 저장
  const handleSave = () => {
    onSave({ name, description, price, mainImages, detailImages, options });
    setName(''); setDescription(''); setPrice(''); setMainImages([]); setDetailImages([]); setOptions([]);
    onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: styles.paper }}>
      <DialogTitle sx={{ color: styles.text.primary }}>{t('sellerProducts.newProduct')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TextField label={t('sellerProducts.productNameLabel')} value={name} onChange={e => setName(e.target.value)} fullWidth required sx={styles.input} />
          <TextField label={t('sellerProducts.descriptionLabel')} value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline minRows={3} sx={styles.input} />
          <TextField label={t('sellerProducts.priceLabel')} value={price} onChange={e => setPrice(e.target.value)} type="number" fullWidth required sx={styles.input} />
          <Box>
            <Typography fontWeight={700} mb={1} color={styles.text.primary}>{t('sellerProducts.mainImages')}</Typography>
            <input type="file" multiple accept="image/*" onChange={handleMainImageChange} style={{ display: 'block', marginBottom: 8 }} />
            <Stack direction="row" spacing={1}>
              {mainImages.map((file, idx) => (
                <Box key={idx} sx={{ position: 'relative' }}>
                  <img src={URL.createObjectURL(file)} alt="main" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                  <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: isDarkMode ? '#404040' : '#fff', color: styles.text.primary }} onClick={() => handleRemoveMainImage(idx)}><Close fontSize="small" /></IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography fontWeight={700} mb={1} color={styles.text.primary}>{t('sellerProducts.detailImages')}</Typography>
            <input type="file" multiple accept="image/*" onChange={handleDetailImageChange} style={{ display: 'block', marginBottom: 8 }} />
            <Stack direction="row" spacing={1}>
              {detailImages.map((file, idx) => (
                <Box key={idx} sx={{ position: 'relative' }}>
                  <img src={URL.createObjectURL(file)} alt="detail" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                  <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: isDarkMode ? '#404040' : '#fff', color: styles.text.primary }} onClick={() => handleRemoveDetailImage(idx)}><Close fontSize="small" /></IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography fontWeight={700} mb={1} color={styles.text.primary}>{t('sellerProducts.optionsLabel')}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField label={t('sellerProducts.color')} value={optionInput.color} onChange={e => setOptionInput({ ...optionInput, color: e.target.value })} size="small" sx={styles.input} />
              <TextField label={t('sellerProducts.size')} value={optionInput.size} onChange={e => setOptionInput({ ...optionInput, size: e.target.value })} size="small" sx={styles.input} />
              <TextField label={t('sellerProducts.quantity')} value={optionInput.quantity} onChange={e => setOptionInput({ ...optionInput, quantity: e.target.value })} type="number" size="small" sx={styles.input} />
              <Button variant="outlined" onClick={handleAddOption} sx={{
                borderColor: styles.table.cell.borderColor,
                color: styles.text.primary,
                '&:hover': {
                  borderColor: styles.text.primary,
                  bgcolor: isDarkMode ? '#404040' : '#f5f5f5'
                }
              }}>{t('sellerProducts.addOption')}</Button>
            </Stack>
            <Stack spacing={1} mt={1}>
              {options.map((opt, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: isDarkMode ? '#404040' : '#f7f7f7', borderRadius: 2, p: 1 }}>
                  <Typography color={styles.text.primary}>{opt.color} / {opt.size} / {opt.quantity}</Typography>
                  <IconButton size="small" onClick={() => handleRemoveOption(idx)} sx={{ color: styles.text.primary }}><Close fontSize="small" /></IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: styles.text.secondary }}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={handleSave} sx={styles.button.primary}>{t("common.save")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SellerProducts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [products, setProducts] = useState<Product[]>([]);
  const [productOptions, setProductOptions] = useState<{ [key: number]: ProductOption[] }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState(0);
  const [newProductOpen, setNewProductOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // 삭제 확인 팝업 상태 추가
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<number | null>(null);

  // 옵션 편집 상태 추가
  const [editingOption, setEditingOption] = useState<ProductOption | null>(null);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [deleteOptionDialogOpen, setDeleteOptionDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<number | null>(null);

  // 페이지네이션 상태 추가
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  // 링크 복사 snackbar 상태
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // 상품 데이터 로드
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        console.log('=== 상품 데이터 로드 시작 ===');

        // useAuth에서 사용자 정보 가져오기
        if (!user) {
          console.error('❌ 사용자 정보를 가져올 수 없습니다.');
          setLoading(false);
          return;
        }
        console.log('✅ 사용자 정보:', user.id);

        // seller_profile에서 seller 정보 가져오기
        const { data: sellerProfile, error: profileError } = await supabase
          .from('seller_profile')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (profileError || !sellerProfile) {
          console.error('❌ Seller Profile 정보를 찾을 수 없습니다:', profileError);
          return;
        }

        console.log('✅ Seller Profile ID:', sellerProfile.id);

        // 전체 상품 데이터 조회
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .eq('seller_id', sellerProfile.id)
          .order('created_at', { ascending: false });

        if (productsError) {
          console.error('❌ 상품 데이터 로드 실패:', productsError);
          return;
        }

        console.log('📦 조회된 상품 개수:', productsData?.length || 0);

        if (productsData && productsData.length > 0) {
          setProducts(productsData);

          // 모든 상품의 옵션 데이터를 한번에 조회
          console.log('🔍 모든 상품의 옵션 데이터 조회 시작...');
          const productIds = productsData.map(p => p.product_id);

          const { data: allOptionsData, error: optionsError } = await supabase
            .from('product_options')
            .select('*')
            .in('product_id', productIds);

          if (optionsError) {
            console.error('❌ 옵션 데이터 조회 실패:', optionsError);
            setProductOptions({});
          } else {
            // 옵션 데이터를 product_id별로 그룹화
            const optionsByProduct: { [key: number]: ProductOption[] } = {};
            allOptionsData?.forEach(option => {
              if (!optionsByProduct[option.product_id]) {
                optionsByProduct[option.product_id] = [];
              }
              optionsByProduct[option.product_id].push(option);
            });

            setProductOptions(optionsByProduct);
            console.log('✅ 옵션 데이터 로드 완료:', Object.keys(optionsByProduct).length, '개 상품의 옵션');
          }
        } else {
          console.log('⚠️ 현재 사용자에게 등록된 상품이 없습니다.');
          setProducts([]);
          setProductOptions({});
        }

        console.log('✅ 상품 데이터 로드 완료');
      } catch (error) {
        console.error('❌ 데이터 로드 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProducts();
    }
  }, [user]);

  const handleAddProduct = async () => {
    try {
      // useAuth에서 사용자 정보 가져오기
      if (!user) {
        alert(t('common.loginRequired'));
        return;
      }

      // seller_profile에서 seller 정보 가져오기
      const { data: sellerProfile, error: profileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !sellerProfile) {
        alert(t('sellerProducts.sellerNotFound'));
        return;
      }

      // seller_store 테이블에서 해당 seller_id가 존재하는지 확인
      const { data: storeData, error: storeError } = await supabase
        .from('seller_store')
        .select('id')
        .eq('seller_id', sellerProfile.id)
        .single();

      if (storeError || !storeData) {
        // 상점이 등록되지 않은 경우
        alert(t('sellerProducts.shopFirstRequired'));
        navigate('/seller/shop');
        return;
      }

      // 상점이 존재하면 상품 등록 페이지로 이동
      navigate('/seller/addproducts');
    } catch (error) {
      console.error('상점 확인 중 오류:', error);
      alert(t('sellerProducts.shopCheckError'));
    }
  };

  const handleEditProduct = async (product: Product) => {
    console.log('=== 수정 버튼 클릭 ===');
    console.log('선택된 상품:', product);
    
    // 이미 수정 중이면 중복 실행 방지
    if (isEditing) {
      console.log('⚠️ 이미 수정 중입니다.');
      return;
    }
    
    setIsEditing(true);
    setEditingProductId(product.product_id);
    
    try {
      // 해당 상품의 옵션 데이터를 먼저 로드
      console.log('🔍 수정을 위한 옵션 데이터 로드 중...');
      const { data: optionsData, error: optionsError } = await supabase
        .from('product_options')
        .select('*')
        .eq('product_id', product.product_id);

      if (optionsError) {
        console.error('❌ 옵션 데이터 로드 실패:', optionsError);
        alert(t('sellerProducts.optionLoadFailed'));
        return;
      }

      console.log('✅ 옵션 데이터 로드 완료:', optionsData);
      
      // AddProducts 페이지로 상품 데이터를 전달하여 수정 모드로 이동
      const productData = {
        product_id: product.product_id,
        product_name: product.product_name,
        price: product.price,
        status: product.status,
        main_images: product.main_images || [],
        detail_images: product.detail_images || [],
        store_name: product.store_name,
        description: product.description || '',
        keywords: product.keywords || [],
        options: optionsData || [],
        is_d2c: product.is_d2c ?? false,
        external_url: product.external_url || '',
      };
      
      console.log('전달할 상품 데이터:', productData);
      console.log('상세 이미지 확인:', productData.detail_images);
      
      // 데이터 유효성 검증
      if (!productData.product_id || !productData.product_name) {
        console.error('❌ 상품 데이터 유효성 검증 실패:', {
          product_id: productData.product_id,
          product_name: productData.product_name
        });
        alert(t('sellerProducts.productDataIncomplete'));
        return;
      }
      
      console.log('✅ 데이터 유효성 검증 통과');
      
      // URL 파라미터로 데이터 전달 (큰 데이터는 sessionStorage 사용)
      try {
        const jsonData = JSON.stringify(productData);
        console.log('JSON 데이터 크기:', jsonData.length, 'bytes');
        sessionStorage.setItem('editProductData', jsonData);
        console.log('✅ sessionStorage에 데이터 저장 완료');
        navigate('/seller/addproducts?mode=edit');
        console.log('✅ AddProducts 페이지로 이동');
      } catch (error) {
        console.error('❌ 상품 데이터 저장 실패:', error);
        alert(t('sellerProducts.productDataSaveFailed'));
      }
    } catch (error) {
      console.error('❌ 수정 준비 중 오류:', error);
      alert(t('sellerProducts.editPrepError'));
    } finally {
      setIsEditing(false);
      setEditingProductId(null);
    }
  };

  // 삭제 확인 함수
  const handleDeleteConfirm = (productId: number) => {
    setProductToDelete(productId);
    setDeleteDialogOpen(true);
  };

  // 실제 삭제 실행 함수 (소프트 삭제로 변경)
  const handleDeleteProduct = async (productId: number) => {
    try {
      console.log(`🗑️ 상품 ${productId}를 휴지통으로 이동합니다...`);
      
      // 상품 상태를 'trashed'로 변경 (소프트 삭제)
      const { error } = await supabase
        .from('products')
        .update({ status: 'trashed' })
        .eq('product_id', productId);

      if (error) {
        console.error('상품 삭제(휴지통 이동) 실패:', error);
        alert(t('sellerProducts.moveToTrashFailed'));
        return;
      }

      // 로컬 상태 업데이트
      setProducts(products.map(p => 
        p.product_id === productId ? { ...p, status: 'trashed' } : p
      ));

      // 팝업 닫기
      setDeleteDialogOpen(false);
      setProductToDelete(null);

      console.log(`✅ 상품 ${productId} 휴지통 이동 완료`);

    } catch (error) {
      console.error('휴지통 이동 중 오류:', error);
      alert('휴지통 이동 중 오류가 발생했습니다.');
    }
  };

  const handleSaveProduct = async (productData: Partial<Product>) => {
    try {
    if (editingProduct) {
        // 기존 상품 업데이트
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('product_id', editingProduct.product_id);

        if (error) {
          console.error('상품 업데이트 실패:', error);
          return;
        }

        // 로컬 상태 업데이트
      setProducts(products.map(p => 
          p.product_id === editingProduct.product_id ? { ...p, ...productData } : p
        ));
    }
    setOpenDialog(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('저장 중 오류:', error);
    }
  };

  // 상태 변경 핸들러
  const handleStatusChange = async (productId: number, newStatus: string) => {
    try {
      console.log(`🔄 상품 상태 변경: ${productId} -> ${newStatus}`);
      
      // 데이터베이스 업데이트
      const { error } = await supabase
        .from('products')
        .update({ status: newStatus })
        .eq('product_id', productId);

      if (error) {
        console.error('상태 변경 실패:', error);
        alert(t('sellerProducts.statusChangeFailed'));
        return;
      }

      // 로컬 상태 업데이트
      setProducts(products.map(p => 
        p.product_id === productId ? { ...p, status: newStatus } : p
      ));

      console.log(`✅ 상품 상태 변경 완료: ${productId} -> ${newStatus}`);
    } catch (error) {
      console.error('상태 변경 중 오류:', error);
      alert(t('sellerProducts.statusChangeError'));
    }
  };

  // 휴지통에서 복원 핸들러
  const handleRestoreProduct = async (productId: number) => {
    try {
      console.log(`🔄 상품 ${productId}를 복원합니다...`);

      // 상품 상태를 'inactive'로 변경 (복원)
      const { error } = await supabase
        .from('products')
        .update({ status: 'inactive' })
        .eq('product_id', productId);

      if (error) {
        console.error('상품 복원 실패:', error);
        alert(t('sellerProducts.restoreFailed'));
        return;
      }

      // 로컬 상태 업데이트
      setProducts(products.map(p =>
        p.product_id === productId ? { ...p, status: 'inactive' } : p
      ));

      console.log(`✅ 상품 ${productId} 복원 완료`);
      alert(t('sellerProducts.restoreSuccess'));

    } catch (error) {
      console.error('상품 복원 중 오류:', error);
      alert(t('sellerProducts.restoreError'));
    }
  };


  // 옵션 수정 핸들러
  const handleEditOption = (option: ProductOption) => {
    setEditingOption(option);
    setOptionDialogOpen(true);
  };

  // 옵션 저장 핸들러
  const handleSaveOption = async (optionData: Partial<ProductOption>) => {
    if (!editingOption) return;

    try {
      console.log('📝 옵션 업데이트 중...', optionData);

      const { error } = await supabase
        .from('product_options')
        .update(optionData)
        .eq('id', editingOption.id);

      if (error) {
        console.error('옵션 업데이트 실패:', error);
        alert(t('sellerProducts.optionUpdateFailed'));
        return;
      }

      // 로컬 상태 업데이트
      setProductOptions(prev => ({
        ...prev,
        [editingOption.product_id]: prev[editingOption.product_id].map(opt =>
          opt.id === editingOption.id ? { ...opt, ...optionData } : opt
        )
      }));

      setOptionDialogOpen(false);
      setEditingOption(null);
      console.log('✅ 옵션 업데이트 완료');
    } catch (error) {
      console.error('옵션 저장 중 오류:', error);
      alert(t('sellerProducts.optionSaveError'));
    }
  };

  // 옵션 삭제 확인
  const handleDeleteOptionConfirm = (optionId: number) => {
    setOptionToDelete(optionId);
    setDeleteOptionDialogOpen(true);
  };

  // 옵션 삭제 실행
  const handleDeleteOption = async (optionId: number) => {
    try {
      console.log(`🗑️ 옵션 ${optionId}를 삭제합니다...`);

      const { error } = await supabase
        .from('product_options')
        .delete()
        .eq('id', optionId);

      if (error) {
        console.error('옵션 삭제 실패:', error);
        alert(t('sellerProducts.optionDeleteFailed'));
        return;
      }

      // 로컬 상태 업데이트
      setProductOptions(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(productId => {
          updated[Number(productId)] = updated[Number(productId)].filter(
            opt => opt.id !== optionId
          );
        });
        return updated;
      });

      setDeleteOptionDialogOpen(false);
      setOptionToDelete(null);
      console.log(`✅ 옵션 ${optionId} 삭제 완료`);
    } catch (error) {
      console.error('옵션 삭제 중 오류:', error);
      alert('옵션 삭제 중 오류가 발생했습니다.');
    }
  };

  // 페이지네이션 핸들러
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Universal Link 복사 핸들러
  const handleCopyLink = async (productId: number, productName: string) => {
    try {
      const url = `https://clozet.my/product/${productId}`;
      await navigator.clipboard.writeText(url);
      setSnackbarMessage(t('sellerProducts.linkCopied', { name: productName }));
      setSnackbarOpen(true);
      console.log('🔗 복사된 링크:', url);
    } catch (error) {
      console.error('링크 복사 실패:', error);
      setSnackbarMessage(t('sellerProducts.linkCopyFailed'));
      setSnackbarOpen(true);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.store_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      (tab === 0 && product.status === 'active') ||
      (tab === 1 && product.status === 'inactive') ||
      (tab === 2 && product.status === 'trashed') ||
      tab === 3; // All
    return matchesSearch && matchesStatus;
  });

  // 페이지네이션 적용
  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // 필터 변경 시 페이지 초기화
  useEffect(() => {
    setPage(0);
  }, [tab, searchTerm]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', bgcolor: 'transparent' }}>
        <Typography color={styles.text.primary}>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight={700} mb={3} sx={styles.title}>
        My Products
      </Typography>
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            placeholder="Search products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: styles.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...styles.input, flexGrow: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddProduct}
            sx={{ ...styles.button.primary, minWidth: 'fit-content' }}
          >
            Add Product
          </Button>
          {/* 개발용 테스트 버튼 */}
          {/* <Button
            variant="outlined"
            onClick={createTestProduct}
            sx={{ minWidth: 'fit-content', borderColor: 'orange', color: 'orange' }}
          >
            테스트 데이터 생성
          </Button> */}
        </Stack>
      </Paper>
      
      {/* 상태별 탭 */}
      <Paper sx={{ ...styles.paper, mb: 2 }}>
        <Tabs 
          value={tab} 
          onChange={(_, newValue) => setTab(newValue)} 
          sx={{ 
            '& .MuiTab-root': { 
              color: styles.text.secondary,
              fontWeight: 600,
              '&.Mui-selected': { 
                color: styles.text.primary 
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: styles.text.primary
            }
          }}
        >
          <Tab label="Active" />
          <Tab label="Inactive" />
          <Tab label="Trashed" />
          <Tab label="All" />
        </Tabs>
      </Paper>
      
      <TableContainer component={Paper} sx={styles.paper}>
        <Table>
          <TableHead>
            <TableRow sx={styles.table.header}>
              <TableCell sx={styles.table.cell}></TableCell>
              <TableCell sx={styles.table.cell}>Product</TableCell>
              <TableCell sx={styles.table.cell}>Price</TableCell>
              <TableCell sx={styles.table.cell}>{t('sellerProducts.totalStock')}</TableCell>
              <TableCell sx={styles.table.cell}>Status</TableCell>
              <TableCell sx={styles.table.cell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.map((product) => (
              <ExpandableRow
                key={product.product_id}
                product={product}
                options={productOptions[product.product_id] || []}
                onEdit={handleEditProduct}
                onDelete={handleDeleteConfirm}
                onStatusChange={handleStatusChange}
                onRestore={handleRestoreProduct}
                isEditing={isEditing}
                editingProductId={editingProductId}
                onEditOption={handleEditOption}
                onDeleteOption={handleDeleteOptionConfirm}
                onCopyLink={handleCopyLink}
              />
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredProducts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('sellerProducts.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </TableContainer>
      
      {/* 상품 편집 다이얼로그 */}
      <ProductDialog
        open={openDialog}
        product={editingProduct}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveProduct}
      />
      <NewProductDialog open={newProductOpen} onClose={() => setNewProductOpen(false)} onSave={() => {/* 저장 로직 */}} />
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => productToDelete && handleDeleteProduct(productToDelete)}
      />

      {/* 옵션 편집 다이얼로그 */}
      <OptionDialog
        open={optionDialogOpen}
        option={editingOption}
        onClose={() => {
          setOptionDialogOpen(false);
          setEditingOption(null);
        }}
        onSave={handleSaveOption}
      />

      {/* 옵션 삭제 확인 다이얼로그 */}
      <DeleteConfirmDialog
        open={deleteOptionDialogOpen}
        onClose={() => setDeleteOptionDialogOpen(false)}
        onConfirm={() => optionToDelete && handleDeleteOption(optionToDelete)}
      />

      {/* 링크 복사 성공 알림 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

interface ProductDialogProps {
  open: boolean;
  product: Product | null;
  onClose: () => void;
  onSave: (productData: Partial<Product>) => void;
}

function ProductDialog({ open, product, onClose, onSave }: ProductDialogProps) {
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [formData, setFormData] = useState({
    product_name: product?.product_name || '',
    store_name: product?.store_name || '',
    price: product?.price || 0,
    status: product?.status || 'pending',
    description: product?.description || '',
  });

  useEffect(() => {
    if (product) {
      setFormData({
        product_name: product.product_name,
        store_name: product.store_name,
        price: product.price,
        status: product.status,
        description: product.description,
      });
    }
  }, [product]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: styles.paper }}>
      <DialogTitle sx={{ color: styles.text.primary }}>
        {product ? 'Edit Product' : 'New Product'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Product Name"
              value={formData.product_name}
              onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Store Name"
              value={formData.store_name}
              onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              minRows={3}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth sx={styles.input}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="trashed">Trashed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: styles.text.secondary }}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" sx={styles.button.primary}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 삭제 확인 팝업 컴포넌트
function DeleteConfirmDialog({ open, onClose, onConfirm }: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: styles.paper }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: styles.text.primary }}>
        {t('sellerProducts.moveToTrash')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: 'center', mb: 2, color: styles.text.primary }}>
          {t('sellerProducts.moveToTrashConfirm')}
        </Typography>
        <Typography sx={{ textAlign: 'center', color: styles.text.secondary }}>
          {t('sellerProducts.restoreFromTrash')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{
          minWidth: 100,
          borderColor: styles.table.cell.borderColor,
          color: styles.text.primary,
          '&:hover': {
            borderColor: styles.text.primary,
            bgcolor: isDarkMode ? '#404040' : '#f5f5f5'
          }
        }}>
          {t('common.cancel')}
        </Button>
        <Button onClick={onConfirm} variant="contained" sx={{
          minWidth: 100,
          bgcolor: '#ff9800',
          color: '#fff',
          '&:hover': {
            bgcolor: '#f57c00'
          }
        }}>
          {t('sellerProducts.moveToTrashBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 옵션 편집 다이얼로그
interface OptionDialogProps {
  open: boolean;
  option: ProductOption | null;
  onClose: () => void;
  onSave: (optionData: Partial<ProductOption>) => void;
}

function OptionDialog({ open, option, onClose, onSave }: OptionDialogProps) {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [formData, setFormData] = useState({
    color_name: option?.color_name || '',
    color_hex: option?.color_hex || '#000000',
    size: option?.size || '',
    quantity: option?.quantity || 0,
  });

  useEffect(() => {
    if (option) {
      setFormData({
        color_name: option.color_name,
        color_hex: option.color_hex,
        size: option.size,
        quantity: option.quantity,
      });
    }
  }, [option]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: styles.paper }}>
      <DialogTitle sx={{ color: styles.text.primary }}>
        {t('sellerProducts.editOption')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('sellerProducts.colorName')}
              value={formData.color_name}
              onChange={(e) => setFormData({ ...formData, color_name: e.target.value })}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="body2" color={styles.text.primary} mb={1}>
                {t('sellerProducts.colorCode')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={formData.color_hex}
                  onChange={(e) => setFormData({ ...formData, color_hex: e.target.value })}
                  style={{
                    width: 60,
                    height: 40,
                    border: `1px solid ${isDarkMode ? '#606060' : '#ddd'}`,
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                />
                <TextField
                  fullWidth
                  value={formData.color_hex}
                  onChange={(e) => setFormData({ ...formData, color_hex: e.target.value })}
                  sx={styles.input}
                  placeholder="#000000"
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('sellerProducts.size')}
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('sellerProducts.quantity')}
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
              sx={styles.input}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: styles.text.secondary }}>{t("common.cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" sx={styles.button.primary}>
          {t('sellerProducts.saveBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 